import { Image, Page, Text, View } from '@react-pdf/renderer'
import { Badge, Space, Tabs } from 'antd'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import {
  AssetDrawingTabContent,
  Indication,
} from 'pages/reporting/reports/raut/sections/subSections/assetDrawingComponents/AssetDrawingTabContent'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoSource } from 'types'

const DrawingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;

    &::before {
      border-bottom-color: ${({ theme }) =>
        theme.forecast.cardDividerBackground};
    }
  }

  .ant-tabs-tab {
    padding: 0.625rem 1rem;
    margin: 0;
    background: ${({ theme }) => theme.forecast.background2};
    border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
    border-radius: 0.375rem 0.375rem 0 0;
    color: ${({ theme }) => theme.forecast.secondaryText};
    transition: all 0.15s ease-out;

    &:hover {
      color: ${({ theme }) => theme.forecast.defaultText};
      background: ${({ theme }) => theme.forecast.background2_5};
    }

    &.ant-tabs-tab-active {
      background: ${({ theme }) => theme.forecast.cardBackground};
      border-bottom-color: ${({ theme }) => theme.forecast.cardBackground};

      .ant-tabs-tab-btn {
        color: ${({ theme }) => theme.forecast.activeText};
      }
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }
`

const TabContentWrapper = styled.div`
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
`

interface CaptureStatus {
  isCapturing: boolean
  message: string
}

const RenderReact: FC = () => {
  const {
    sceneConfigs,
    metadataEntryId,
    inspections,
    componentsByInspection,
    componentModelsByInspection,
    relevantThicknessCDFsByInspection,
    markupLayersByInspection,
    indicationsByInspectionId,
    assetModel: selectedAssetModel,
    handleAssetDrawingsFinish,
    assetDrawings,
    nominalsByInspection,
    formData,
    hasPhasedArrayInspection,
  } = useRautReportOps()

  const isLocked = useMemo(() => {
    return formData.lockState?.isLocked || false
  }, [formData.lockState])

  const heatmapSceneConfig = sceneConfigs?.heatmapSceneConfig
  const noHeatmapSceneConfig = sceneConfigs?.noHeatmapSceneConfig

  // Active tab state
  const [activeTabKey, setActiveTabKey] = useState<string>(
    inspections[0]?.id || ''
  )

  // Get data for the active inspection
  const selectedInspection = useMemo(
    () => inspections.find((i) => i.id === activeTabKey),
    [inspections, activeTabKey]
  )
  const selectedComponent = useMemo(
    () => componentsByInspection.get(activeTabKey),
    [componentsByInspection, activeTabKey]
  )
  const selectedComponentModel = useMemo(
    () => componentModelsByInspection.get(activeTabKey),
    [componentModelsByInspection, activeTabKey]
  )
  const relevantThicknessCDF = useMemo(
    () => relevantThicknessCDFsByInspection.get(activeTabKey),
    [relevantThicknessCDFsByInspection, activeTabKey]
  )
  const markupLayers = useMemo(
    () => markupLayersByInspection.get(activeTabKey) || [],
    [markupLayersByInspection, activeTabKey]
  )
  const indications = useMemo(
    () => indicationsByInspectionId.get(activeTabKey) || [],
    [indicationsByInspectionId, activeTabKey]
  )

  const [captureStatusByInspection, setCaptureStatusByInspection] = useState<
    Record<string, CaptureStatus>
  >({})

  const handleCaptureStatusChange = useCallback(
    (status: CaptureStatus) => {
      setCaptureStatusByInspection((prev) => ({
        ...prev,
        [activeTabKey]: status,
      }))
    },
    [activeTabKey]
  )

  const getCaptureProgress = useCallback(
    (inspectionId: string) => {
      const status = captureStatusByInspection[inspectionId]
      const isInProgress = status?.isCapturing || false

      // Check if all required drawing types are complete for this inspection
      //NOTE: This is not a thorough check. Since we can have multiple images in these arrays,
      //it's possible for partially complete states to get marked as complete
      //To check for full complete-ness we'd need to do the chunking calcs ahead of time.
      const drawings = assetDrawings?.get(inspectionId)

      // Check if this specific inspection is PAUT
      const inspection = inspections.find((i) => i.id === inspectionId)
      const isInspectionPAUT =
        inspection?.data.inspection_type === GeckoSource.PhasedArray.id

      if (isInspectionPAUT) {
        const isComplete = !!(
          drawings &&
          drawings.withWeldsNoHeatmap &&
          drawings.withWeldsNoHeatmap.length > 0
        )
        return {
          isComplete,
          isInProgress,
        }
      }

      // Rapid AUT inspections need heatmap, no-heatmap, and indications
      const baseComplete = !!(
        drawings &&
        drawings.heatmap &&
        drawings.heatmap.length > 0 &&
        drawings.noHeatmap &&
        drawings.noHeatmap.length > 0 &&
        drawings.withIndicationsHeatmap &&
        drawings.withIndicationsHeatmap.length > 0
      )

      return {
        isComplete: baseComplete,
        isInProgress,
      }
    },
    [captureStatusByInspection, assetDrawings, inspections]
  )

  const tabItems = useMemo(
    () =>
      inspections.map((inspection) => {
        const component = componentsByInspection.get(inspection.id)
        const componentName = component?.data.name || 'Unknown Component'
        const progress = getCaptureProgress(inspection.id)

        return {
          key: inspection.id,
          label: (
            <Space>
              {`${componentName} (${inspection.data.inspection_type})`}
              {progress.isComplete && <Badge status='success' />}
              {progress.isInProgress && <Badge status='processing' />}
            </Space>
          ),
          children: null,
        }
      }),
    [inspections, componentsByInspection, getCaptureProgress]
  )

  const tabContent = useMemo(() => {
    if (
      !selectedComponent ||
      !selectedComponentModel ||
      !heatmapSceneConfig ||
      !noHeatmapSceneConfig ||
      !selectedInspection
    ) {
      return null
    }

    return (
      <AssetDrawingTabContent
        isLocked={isLocked}
        key={activeTabKey}
        inspection={selectedInspection}
        inspectionId={activeTabKey}
        component={selectedComponent!}
        componentModel={selectedComponentModel!}
        thicknessCDF={relevantThicknessCDF}
        markupLayers={markupLayers}
        indications={indications as Indication[]}
        assetModel={selectedAssetModel}
        heatmapSceneConfig={heatmapSceneConfig}
        noHeatmapSceneConfig={noHeatmapSceneConfig}
        metadataEntryId={metadataEntryId}
        hasPhasedArrayInspection={hasPhasedArrayInspection}
        onDrawingFinish={handleAssetDrawingsFinish}
        onCaptureStatusChange={handleCaptureStatusChange}
        nominal={nominalsByInspection.get(activeTabKey) as GeckoMeasurement}
      />
    )
  }, [
    selectedInspection,
    selectedComponent,
    selectedComponentModel,
    relevantThicknessCDF,
    heatmapSceneConfig,
    noHeatmapSceneConfig,
    activeTabKey,
    markupLayers,
    indications,
    selectedAssetModel,
    metadataEntryId,
    hasPhasedArrayInspection,
    handleAssetDrawingsFinish,
    handleCaptureStatusChange,
    isLocked,
    nominalsByInspection,
  ])

  return (
    <DrawingContainer>
      <StyledTabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={tabItems}
        size='small'
        type='card'
        tabBarGutter={8}
      />
      <TabContentWrapper>{tabContent}</TabContentWrapper>
    </DrawingContainer>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    assetDrawings: drawingsByInspection,
    componentsByInspection,
  } = useRautReportOps()

  const hasRegisteredAssetDrawing = useRef(false)

  if (!drawingsByInspection) return null

  return (
    <>
      {Array.from(drawingsByInspection.entries()).map(
        ([inspectionId, drawings]) => {
          const component = componentsByInspection.get(inspectionId)
          const componentName = component?.data.name || 'Unknown Component'

          return (
            <React.Fragment key={inspectionId}>
              {/* No Heatmap Drawing (Scan Plan) */}
              {drawings?.noHeatmap &&
                drawings.noHeatmap.map((noHeatmap, index) => {
                  return (
                    <Page
                      key={`${inspectionId}-noHeatmap-${index}`}
                      size='A4'
                      orientation='landscape'
                      style={PDFStyles.page}
                      id={
                        index === 0 && !hasRegisteredAssetDrawing.current
                          ? 'assetDrawing'
                          : `assetDrawing-${inspectionId}-noHeatmap-${index}`
                      }
                      wrap={false}>
                      {/* Hidden text for page tracking - only register if we haven't yet */}
                      <Text
                        render={({ pageNumber }) => {
                          if (!hasRegisteredAssetDrawing.current) {
                            onRender({ id: 'assetDrawing', pageNumber })
                            hasRegisteredAssetDrawing.current = true
                          }
                          return ''
                        }}
                      />

                      <PageHeader title={`Asset Drawings - ${componentName}`} />

                      <View style={PDFStyles.content}>
                        <View style={{ alignItems: 'flex-start' }}>
                          <PageSubheader
                            title={`Asset Drawing - Scan Plan`}
                            align='left'
                            color='black'
                          />
                        </View>

                        <Text style={PDFStyles.imageDescription}>
                          Full asset view without thickness heatmap overlay,
                          showing asset geometry, orientation, and scan plan.
                        </Text>

                        <View style={PDFStyles.imageContainer}>
                          <Image
                            src={noHeatmap.signedUrl}
                            style={PDFStyles.image}
                            cache={false}
                          />
                        </View>
                      </View>

                      <PageFooter
                        jobNumber={formData.coverPageData.geckoJobId}
                        revisionNumber={
                          formData.revisionMetadata.revisionNumber
                        }
                        prelim={
                          !(
                            formData.signoff?.reviewerSignoff &&
                            formData.signoff.supervisorSignoff
                          )
                        }
                      />
                    </Page>
                  )
                })}

              {/* Heatmap Drawing (UT Thickness Reading Heat Map) */}
              {drawings?.heatmap &&
                drawings.heatmap.map((heatmap, index) => {
                  return (
                    <Page
                      key={`${inspectionId}-heatmap-${index}`}
                      size='A4'
                      orientation='landscape'
                      style={PDFStyles.page}
                      id={`assetDrawing-${inspectionId}-heatmap-${index}`}
                      wrap={false}>
                      {/* Hidden text for page tracking - only register if we haven't yet */}
                      <Text
                        render={({ pageNumber }) => {
                          if (!hasRegisteredAssetDrawing.current) {
                            onRender({ id: 'assetDrawing', pageNumber })
                            hasRegisteredAssetDrawing.current = true
                          }
                          return ''
                        }}
                      />

                      <PageHeader title={`Asset Drawings - ${componentName}`} />

                      <View style={PDFStyles.content}>
                        <View style={{ alignItems: 'flex-start' }}>
                          <PageSubheader
                            title={`Asset Drawing - UT Thickness Reading Heat Map`}
                            align='left'
                            color='black'
                          />
                        </View>

                        <Text style={PDFStyles.imageDescription}>
                          Full asset view with thickness heatmap overlay showing
                          measured wall thickness.
                        </Text>

                        <View style={PDFStyles.imageContainer}>
                          <Image
                            src={heatmap.signedUrl}
                            style={PDFStyles.image}
                            cache={false}
                          />
                        </View>
                      </View>

                      <PageFooter
                        jobNumber={formData.coverPageData.geckoJobId}
                        revisionNumber={
                          formData.revisionMetadata.revisionNumber
                        }
                        prelim={
                          !(
                            formData.signoff?.reviewerSignoff &&
                            formData.signoff.supervisorSignoff
                          )
                        }
                      />
                    </Page>
                  )
                })}

              {/* Indications Heatmap Drawing (Indications) */}
              {drawings?.withIndicationsHeatmap &&
                drawings.withIndicationsHeatmap.map(
                  (withIndicationsHeatmap, index) => {
                    return !formData.mainInfo.scanIndicationMode ? (
                      <Page
                        key={`${inspectionId}-withIndications-${index}`}
                        size='A4'
                        orientation='landscape'
                        style={PDFStyles.page}
                        id={`assetDrawing-${inspectionId}-withIndications-${index}`}
                        wrap={false}>
                        {/* Hidden text for page tracking - only register if we haven't yet */}
                        <Text
                          render={({ pageNumber }) => {
                            if (!hasRegisteredAssetDrawing.current) {
                              onRender({ id: 'assetDrawing', pageNumber })
                              hasRegisteredAssetDrawing.current = true
                            }
                            return ''
                          }}
                        />
                        <PageHeader
                          title={`Asset Drawings - ${componentName}`}
                        />
                        <View style={PDFStyles.content}>
                          <View style={{ alignItems: 'flex-start' }}>
                            <PageSubheader
                              title={`Asset Drawing - Indications`}
                              align='left'
                              color='black'
                            />
                          </View>

                          <Text style={PDFStyles.imageDescription}>
                            Full asset view with thickness heatmap and all
                            identified indications overlay.
                          </Text>

                          <View style={PDFStyles.imageContainer}>
                            <Image
                              src={withIndicationsHeatmap.signedUrl}
                              style={PDFStyles.image}
                              cache={false}
                            />
                          </View>
                        </View>
                        <PageFooter
                          jobNumber={formData.coverPageData.geckoJobId}
                          revisionNumber={
                            formData.revisionMetadata.revisionNumber
                          }
                          prelim={
                            !(
                              formData.signoff?.reviewerSignoff &&
                              formData.signoff.supervisorSignoff
                            )
                          }
                        />
                      </Page>
                    ) : null
                  }
                )}

              {/* Weld Scan Plan Drawing */}
              {drawings?.withWeldsNoHeatmap &&
                drawings.withWeldsNoHeatmap.map((withWeldsNoHeatmap, index) => {
                  return (
                    <Page
                      key={`${inspectionId}-weldScanPlan-${index}`}
                      size='A4'
                      orientation='landscape'
                      style={PDFStyles.page}
                      id={`assetDrawing-${inspectionId}-weldScanPlan-${index}`}
                      wrap={false}>
                      {/* Hidden text for page tracking - only register if we haven't yet */}
                      <Text
                        render={({ pageNumber }) => {
                          if (!hasRegisteredAssetDrawing.current) {
                            onRender({ id: 'assetDrawing', pageNumber })
                            hasRegisteredAssetDrawing.current = true
                          }
                          return ''
                        }}
                      />
                      <PageHeader title={`Asset Drawings - ${componentName}`} />
                      <View style={PDFStyles.content}>
                        <View style={{ alignItems: 'flex-start' }}>
                          <PageSubheader
                            title={`Asset Drawing - PAUT Scan Plan`}
                            align='left'
                            color='black'
                          />
                        </View>

                        <Text style={PDFStyles.imageDescription}>
                          Full asset view with weld scan overlay showing phased
                          array ultrasonic testing scan locations.
                        </Text>

                        <View style={PDFStyles.imageContainer}>
                          <Image
                            src={withWeldsNoHeatmap.signedUrl}
                            style={PDFStyles.image}
                            cache={false}
                          />
                        </View>
                      </View>
                      <PageFooter
                        jobNumber={formData.coverPageData.geckoJobId}
                        revisionNumber={
                          formData.revisionMetadata.revisionNumber
                        }
                        prelim={
                          !(
                            formData.signoff?.reviewerSignoff &&
                            formData.signoff.supervisorSignoff
                          )
                        }
                      />
                    </Page>
                  )
                })}
            </React.Fragment>
          )
        }
      )}
    </>
  )
}

export const AssetDrawings: ReportSection = {
  id: 'assetDrawing',
  title: 'Asset Drawings',
  description: 'Capture full asset view',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
